/**
 * AppConfig — per-app configuration consumed by shared Map / Table / Dashboard
 * shells via the appStore.
 */

import type { Component } from 'svelte';
import type { LegendSection } from '#lib/components/map/legend/types.js';
import type { Map as MapLibreMap } from 'maplibre-gl';
import type { FeatureCollection } from 'geojson';
import type { HeaderItem } from '#lib/components/Header.svelte';
import type { MenuItem } from '#lib/components/MenuButton.svelte';
import type { MapLayer } from '#lib/stores/legendStore.svelte.js';
import type { FeatureRow, TableColumnDef } from '#lib/components/table/tanstackFeatures.js';
import type { MeasureUnits } from '#lib/components/map/controls/Measure/measureLabels.js';
import type { PanelWidth } from '#lib/panelWidth.js';
import type { AttributesLayoutHelper } from '#lib/components/Attributes.types.js';

export type AppId =
	| 'safecurves'
	| 'safeintersections'
	| 'saferoads'
	| 'ableymire'
	| 'gis-services'
	| 'ableylinks'
	| 'nsw-rlgsp';

// The core app is a spatial platform hosting several product suites. A product
// suite is a named, separately-versioned bundle of apps (e.g. SafeSystem =
// SafeCurves + SafeRoads + …). Apps in no suite are shared platform tools
// (see `PLATFORM_TOOLS`).
export type ProductSuiteId = 'safesystem' | 'mire' | 'aadt' | 'speedmanagement';

export interface ProductSuite {
	id: ProductSuiteId;
	// Display name — drives landing-page headings and header menu titles.
	name: string;
	// Apps in display order. Single source of truth for suite membership AND
	// ordering — there is no per-app `suite` field to keep in sync.
	apps: readonly AppId[];
}

// Product suites in display order (landing page, header menus). Add or reorder
// suites here; the header and landing page iterate this list generically.
export const PRODUCT_SUITE_APPS = [
	{
		id: 'safesystem',
		name: 'SafeSystem',
		apps: ['safecurves', 'saferoads', 'safeintersections']
	},
	{ id: 'mire', name: 'MIRE', apps: ['ableymire'] },
	{ id: 'aadt', name: 'AADT', apps: ['ableylinks'] },
	{ id: 'speedmanagement', name: 'Speed Management', apps: ['nsw-rlgsp'] }
] as const satisfies readonly ProductSuite[];

// Shared platform tools — apps in no product suite. Shown in a dedicated
// "Tools" section/menu, listing unavailable tools as disabled unless the active
// app sets `header.hideUnavailableTools`.
export const PLATFORM_TOOLS = ['gis-services'] as const satisfies readonly AppId[];

// `configData` is the client's boolean feature flags (`getConfiguration`); `summaryData` is narrowed per app.
export type ConfigData = Record<string, boolean>;
export type SummaryData = unknown;

// Dashboard subtitle and issue-report area, for apps whose summary carries a `location_label`.
export const locationLabel = (summaryData: SummaryData): string | undefined =>
	(summaryData as { location_label?: string } | null | undefined)?.location_label;

export interface ModuleDefinition {
	id: string;
	label: string;
	// Raw SVG (e.g. `import x from './x.svg?raw'`). Optional — modules without an
	// icon render label-only in module tabs.
	icon?: string;
}

interface LegendConfig {
	moduleId?: string;
	// Prop shapes differ per legend panel, so the type is loose like `WidgetConfig`.
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	component: Component<any>;
	props?: Record<string, unknown>;
}

export type FilterEnumOption = {
	value: string;
	label: string;
	// Optional: when this option is toggled, also toggle these other values to
	// the same checked state. Implements an "Other" entry that controls a long
	// tail of un-shown codes (e.g. SafeCurves' grouped Ownership column).
	expandsTo?: string[];
};

export type FilterConfig = {
	// Used as the MapLibre property field name when building the filter
	// expression (`['get', id]`).
	id: string;
	label: string;
	// Full universe of values — drives the MapLibre filter expression and
	// the Select-all/Reset-all actions.
	options: FilterEnumOption[];
	// Optional override of which entries to show in the panel. When
	// omitted, the panel renders `options` directly. Use `expandsTo` on
	// individual entries to group long-tail values behind one row.
	displayOptions?: FilterEnumOption[];
};

export type WidgetConfig = {
	id: string;
	// Widgets receive the active SummaryData and pull what they need. Prop shapes
	// differ per widget, so the type is intentionally loose — the dashboard shell
	// passes `summaryData` through and lets each widget narrow it.
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	component: Component<any>;
	// Static props passed alongside `summaryData`.
	props?: Record<string, unknown>;
};

interface MapConfig<TSystem = unknown> {
	// Hook fired after MapLibre's `load` event. Used to register custom marker
	// images, additional sources, or other one-shot setup.
	onLoad?: (map: MapLibreMap) => void | Promise<void>;
	// Bounding box (lng/lat: [minx, miny, maxx, maxy]) used by the Home
	// control and the first-mount fit-to-data. Returns undefined to skip both.
	getDataBounds?: (systemData: TSystem) => [number, number, number, number] | undefined;
	// Initial map bounds (lng/lat: [minx, miny, maxx, maxy]) shown before data
	// loads and used as the fallback when there are no data bounds. Falls back
	// to a continental-US default in Map.svelte when omitted.
	defaultBounds?: [number, number, number, number];
	// Layer id below which the basemap labels are moved (see LABEL_LAYER_IDS).
	// Omit (or return undefined) to leave them at the top of the layer stack.
	getLabelsAnchorLayerId?: (systemData: TSystem) => string | undefined;
	// Source id for an app-managed GeoJSON layer that the customCrashesStore
	// pushes data into. Apps without an upload-crashes feature omit this.
	customCrashesSourceId?: string;
	// App-managed GeoJSON (source id → data), re-pushed into the map whenever the reactive data changes.
	geojsonSources?: () => Record<string, FeatureCollection>;
	// Per-app components mounted inside `.mapContainer` while `view === 'map'`.
	// Each renders with no props and owns its absolute positioning. SafeCurves
	// omits this; MIRE supplies `[MireDataExplorer]`.
	extraControls?: Component[];
	// Measure labels, scale bar and export scale bar units (default `DEFAULT_MEASURE_UNITS`); may be a reactive getter, as SafeCurves derives it from `unitSystemStore`.
	measureUnits?: MeasureUnits;
	// Fired after `syncVisibleLayers` on each legend/data-filter change, letting an
	// app keep extra map layers (e.g. a low-zoom raster) in step with legend state.
	onLayersSynced?: (map: MapLibreMap, layers: MapLayer[]) => void;
	// Zoom below which the data filter control is disabled, for apps that show an
	// unfilterable raster at low zoom. Omit to keep the filter enabled at all zooms.
	dataFilterMinZoom?: number;
	// True when the app is showing data through a source `queryRenderedFeatures`
	// can't see (e.g. a low-zoom WMS raster), so the "no map features" popover is
	// suppressed even though no queryable features rendered.
	hasNonQueryableData?: (zoom: number, layers: MapLayer[]) => boolean;
}

interface DashboardConfig {
	widgets: WidgetConfig[];
	// URL of the dashboard background image (e.g. `/landing_bg.avif` from
	// `static/`). Applied as `background-image` on the dashboard container.
	backgroundImage?: string;
}

// One tab in the Table view. The shared TableContainer renders the active tab's
// `columns` and queries the MapLibre canvas for that tab's layer via
// `getLayerId`, which the app implements in terms of its own SystemData shape.
export interface TableTabConfig<TSystem = unknown> {
	id: string;
	// Tab button label.
	label: string;
	// Singular noun used in the empty state and pagination info
	// (e.g. "curve" → "No curves shown on map", "Showing 1-10 of 42 curves").
	rowLabel: string;
	columns: TableColumnDef<FeatureRow>[];
	// Resolves the MapLibre layer id(s) whose rendered features feed this tab —
	// an array when one tab spans several layers drawing the same root feature
	// (rows are deduped by feature id). Returns undefined when the layer isn't
	// yet present in systemData.
	getLayerId: (systemData: TSystem) => string | string[] | undefined;
	// Set when the dataset is only published for some areas: the tab is hidden
	// altogether (table view and export modal) when `getLayerId` resolves
	// nothing, rather than showing a permanently empty tab.
	optional?: boolean;
}

interface TableConfig<TSystem = unknown> {
	tabs: TableTabConfig<TSystem>[];
	// Tab id activated when the table view first mounts (or when the
	// remembered `tableViewStore.activeTab` doesn't match this app's tabs).
	defaultTabId: string;
	// Optional placeholder for the global filter input.
	filterPlaceholder?: string;
	// Where the tab buttons sit: a horizontal row above the table ('top',
	// the default) or a vertical stack beside it ('left') — use 'left' when
	// an app has too many tabs to fit in one row.
	tabPlacement?: 'top' | 'left';
}

// MapTiler basemap each app renders under its data. The two style URLs live in
// `.env` (`PUBLIC_BASEMAP_NO_ROADS` / `PUBLIC_BASEMAP_FULL`); the active app's
// choice is resolved to a style URL in `#lib/config` (`basemapStyleUrls`).
export type Basemap = 'no-roads' | 'full';

// Per-app satellite imagery: a cached Esri MapServer serving Web Mercator tiles
// at `/tile/{z}/{y}/{x}` (resolved to a raster source by `satelliteSource`).
// Adding one also means adding its origin to `connect-src` / `img-src` in
// `svelte.config.js`, otherwise CSP blocks the tiles.
export type SatelliteBasemap = {
	// MapServer root, no trailing slash.
	url: string;
	// Top cache LOD (`maxLOD` in the service JSON), so MapLibre overzooms past it
	// rather than requesting levels the cache lacks. Only needed when that LOD is
	// below `mapMaxZoom`.
	maxzoom?: number;
	// Cache footprint as lng/lat [minx, miny, maxx, maxy], from `fullExtent`.
	bounds: [number, number, number, number];
	attribution: string;
};

export interface AppConfig<TSystem = unknown> {
	id: AppId;
	name: string;
	basePath: string;
	// Page `<title>` and description/keywords meta tags.
	head: { title: string; description: string; keywords: string };
	/** Raw SVG from $lib/assets/logos, rendered by Logo.svelte. */
	logo: string;

	// Which MapTiler basemap this app's map renders. Required so the choice is
	// explicit; most apps use 'no-roads', AbleyLinks uses 'full'.
	basemap: Basemap;

	// Imagery behind the satellite toggle. Omit to use the platform MapTiler
	// imagery (`PUBLIC_SATELLITE_BASEMAP`); both render through the same toggle.
	satelliteBasemap?: SatelliteBasemap;

	// Per-app version, shown in the Help menu as "Application: v<version>" when
	// this app is active. Bumped alongside the app's own CHANGELOG file (e.g.
	// `src/lib/apps/safesystem/safecurves/CHANGELOG-SafeCurves.md`).
	version: string;

	// Release-notes URL shown in the Help menu when this app is active. Omit and
	// the Help menu shows no release-notes entry at all.
	releaseNotesLink?: string;

	// GeoServer datasets this app owns; available when any is fetched for the key (none = no constraint).
	datasets: string[];

	loaders: {
		// Null when the key has none of this app's data, which shows the "Data not available" dialog; throw on failure.
		system: (apiKey: string) => Promise<TSystem | null>;
		// Optional per-app summary loader. The shared shell calls this when the API
		// key becomes available, storing the result on `geodataStore.summaryData`.
		// Apps without a summary endpoint omit it — the shell skips the call.
		summary?: (apiKey: string) => Promise<SummaryData>;
	};

	modules?: ModuleDefinition[];
	legends: LegendConfig[];

	/**
	 * Descriptor for the read-only active-layers legend (bottom-left map button, or
	 * a module tab). Also drives the map-export legend, so it is data, not a component.
	 */
	legendSections?: LegendSection[];
	/** Sprite-sheet path for icon swatches in the active legend (rlgsp). */
	legendSpritePath?: string;

	// Projects this app's `TSystem` into the `MapLayer[]` shape consumed by `legendStore`; apps without legend layers omit it.
	parseLegendLayers?: (payload: TSystem) => MapLayer[];

	// Optional Map shell hooks. Apps needing post-load setup, an initial
	// data-area zoom, or a custom-crashes source bind those through this slot.
	// Omit when the app uses none of them.
	map?: MapConfig<TSystem>;

	table: TableConfig<TSystem>;

	filters: FilterConfig[];

	dashboard: DashboardConfig;

	// Per-app header slots only; shared menus are built in `src/routes/+layout.svelte`.
	header: {
		dashboard?: HeaderItem;
		// Gates on a runtime flag in `configData` (SafeCurves' Programs gates on `work_programs`); null when hidden.
		programs?: (configData: ConfigData) => HeaderItem | null;
		method?: (system: TSystem) => HeaderItem | null;
		playbook?: (system: TSystem) => HeaderItem | null;
		// Extra app-owned menus, rendered after the slots above; a builder so it can read `APPS` without a circular-import TDZ.
		menus?: () => HeaderItem[];
		// Drop `PLATFORM_TOOLS` entries this api key can't use from the shared Tools
		// menu (hiding the menu outright when none remain), instead of the default of
		// listing them disabled. For apps whose audience has no path to buying them.
		hideUnavailableTools?: boolean;
		// Help menu entries; defaults to the shared "Get support" item.
		help?: MenuItem[];
	};

	attributes: {
		/** Custom panel; leave null and set `columns` for the default FeatureAttributesPanel. */
		panel: Component | null;
		/** Attribute layouts keyed by system layer id, rendered by the default panel. */
		columns?: Record<string, AttributesLayoutHelper>;
		countermeasures: Component | null;
		/**
		 * Opt this app into the "Report data issue" button in the attributes
		 * panel. Omitted/false = no button. Also requires the reporter feature
		 * enabled for the env (PUBLIC_ISSUE_REPORTER_ENABLED; the URL is derived
		 * from PUBLIC_API_HOST — see `issueReporterUrl` in $lib/config).
		 */
		issueReporting?: boolean;
		/**
		 * Side-panel width at the widest (≥1100px) breakpoint. Defaults to 640;
		 * apps whose panel hosts dense UI override it (SafeIntersections uses 820
		 * for the interventions calculator). Narrower breakpoints keep the platform
		 * defaults.
		 */
		panelWidth?: PanelWidth;
	};

	// Optional AI data-assistant slide-out. Apps opt in by providing the panel
	// component; the shared shell renders the toggle + container when present
	// (and the AI build flag is on).
	dataChat?: Component;
}
